import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { queryClient } from '@/queryclient';
import { useMutation, useQuery } from '@/utils/api';

interface NarrativesContent {
  content: string;
  lastModified: Date | null;
}

/**
 * Fetches narratives.md from the customer's S3 bucket via presigned URL.
 * Returns content and last modified date.
 * Handles 404/empty gracefully (returns empty content, not error).
 */
export function useGetNarratives(email?: string) {
  const axios = useAxios({ email });

  return useQuery<NarrativesContent>({
    defaultErrorMessage: 'Failed to fetch narratives',
    queryKey: ['narratives', email],
    queryFn: async () => {
      try {
        const res = await axios.get<{ url: string }>(`/file`, {
          params: { name: 'narratives.md' },
        });

        const presignedUrl = res.data.url;

        if (!presignedUrl) {
          return { content: '', lastModified: null };
        }

        const fileResponse = await fetch(presignedUrl);

        if (!fileResponse.ok) {
          return { content: '', lastModified: null };
        }

        const lastModifiedHeader = fileResponse.headers.get('Last-Modified');
        const lastModified = lastModifiedHeader
          ? new Date(lastModifiedHeader)
          : null;

        const content = await fileResponse.text();

        return { content: content || '', lastModified };
      } catch {
        return { content: '', lastModified: null };
      }
    },
    retry: 0,
  });
}

/**
 * Uploads a narratives.md file to the customer's S3 bucket.
 * Gets a presigned PUT URL then uploads directly to S3.
 * On success: shows toast and invalidates narratives + file queries.
 */
export function useUploadNarratives(email?: string) {
  const axios = useAxios({ email });
  const { invalidate: invalidateMyFiles } = useMy(
    { resource: 'file' },
    { enabled: false, email }
  );

  return useMutation({
    defaultErrorMessage: 'Failed to upload narratives',
    mutationFn: async (file: File | string) => {
      const content = typeof file === 'string' ? file : await file.text();

      const res = await axios.put<{ url: string }>(`/file`, null, {
        params: { name: 'narratives.md' },
      });

      const uploadUrl = res.data.url;

      if (!uploadUrl) {
        throw new Error('Failed to get upload URL');
      }

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: content,
        headers: { 'Content-Type': 'text/markdown' },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      return null;
    },
    onSuccess: () => {
      toast.success('Narratives uploaded successfully');
      queryClient.invalidateQueries({ queryKey: ['narratives', email] });
      invalidateMyFiles();
    },
  });
}

/**
 * Deletes the narratives.md file from the customer's S3 bucket.
 * On success: shows toast and invalidates narratives + file queries.
 */
export function useDeleteNarratives(email?: string) {
  const axios = useAxios({ email });
  const { invalidate: invalidateMyFiles } = useMy(
    { resource: 'file' },
    { enabled: false, email }
  );

  return useMutation({
    defaultErrorMessage: 'Failed to delete narratives',
    mutationFn: async () => {
      await axios.delete(`/file`, {
        params: { name: 'narratives.md' },
      });
      return null;
    },
    onSuccess: () => {
      toast.success('Narratives deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['narratives', email] });
      invalidateMyFiles();
    },
  });
}
